import { NavigateFunction } from 'react-router-dom';
import { ThemeMode } from './theme';
import { portfolioKnowledge } from '../utils/portfolioKnowledge';

export type PagePath = '/' | '/about' | '/services' | '/projects' | '/contact';

export const PAGE_PATHS: PagePath[] = ['/', '/about', '/services', '/projects', '/contact'];

export type OpenTarget = 'github' | 'linkedin' | 'facebook' | 'whatsapp' | 'email' | 'call' | 'resume';

export type AssistantAction =
  | { type: 'navigate'; path: PagePath }
  | { type: 'scroll'; direction: 'up' | 'down' }
  | { type: 'scrollTo'; section: string }
  | { type: 'theme'; theme: ThemeMode }
  | { type: 'open'; target: OpenTarget }
  | { type: 'stopSpeaking' };

export interface SectionTarget {
  id: string;
  label: string;
  path: PagePath;
  keywords: string[];
}

export const SECTIONS: SectionTarget[] = [
  { id: 'skills', label: 'Skills', path: '/', keywords: ['skills section', 'skills dikhao', 'skills dikha', 'skills kholo', 'core skills section', 'مہارتیں دیکھو', 'مہارتیں کھولو', 'سکل دیکھو'] },
  { id: 'tech-stack', label: 'Tech Stack', path: '/', keywords: ['tech stack section', 'tech stack dikhao', 'tech stack kholo', 'ٹیک دیکھو', 'ٹیکنالوجی دیکھو'] },
  { id: 'projects', label: 'Projects', path: '/projects', keywords: ['projects section', 'project section', 'project section kholo'] },
  { id: 'services', label: 'Services', path: '/services', keywords: ['services section', 'service section'] },
  { id: 'experience', label: 'Experience', path: '/about', keywords: ['experience section', 'work experience section', 'experience dikhao', 'تجربہ دیکھو', 'ایکسپیرینس دیکھو'] },
  { id: 'certificates', label: 'Certificates', path: '/about', keywords: ['certificates section', 'certifications section', 'سرٹیفکیٹ دیکھو'] },
  { id: 'recommendations', label: 'Recommendations', path: '/about', keywords: ['recommendations section', 'testimonials section', 'letters of recommendation'] },
  { id: 'contact', label: 'Contact', path: '/contact', keywords: ['contact section', 'contact form section'] },
];

export const EXTERNAL_LINKS: Record<OpenTarget, string> = {
  github: portfolioKnowledge.socials.github,
  linkedin: portfolioKnowledge.socials.linkedin,
  facebook: portfolioKnowledge.socials.facebook,
  whatsapp: portfolioKnowledge.socials.whatsapp,
  email: portfolioKnowledge.socials.email,
  call: portfolioKnowledge.socials.call,
  resume: portfolioKnowledge.resume,
};

export function scrollToElement(elementId: string): void {
  const el = document.getElementById(elementId);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function scrollWindow(direction: 'up' | 'down'): void {
  const amount = window.innerHeight * 0.8;
  window.scrollBy({ top: direction === 'down' ? amount : -amount, behavior: 'smooth' });
}

function hasWord(input: string, words: string[]): boolean {
  return words.some((word) => input.includes(word));
}

const NAV_VERBS = [
  'kholo',
  'khol',
  'khola',
  'open',
  'jao',
  'jayo',
  'go to',
  'take me to',
  'dikhao',
  'dikha',
  'dikhaye',
  'dikho',
  'dekho',
  'dekh',
  'dikh',
  'دیکھو',
  'دیکھ',
  'دکھاؤ',
  'دکھا',
  'کھولو',
  'جاؤ',
  'لے چلو',
];

function detectNavigation(input: string): PagePath | null {
  const hasVerb = hasWord(input, NAV_VERBS);

  if (hasVerb) {
    if (hasWord(input, ['home', 'ghar', 'گھر', 'ہوم'])) return '/';
    if (hasWord(input, ['about', 'بارے', 'پروفائل', 'پروفائ'])) return '/about';
    if (hasWord(input, ['service', 'سروس', 'خدمات'])) return '/services';
    if (hasWord(input, ['project', 'پروجیکٹ', 'منصوب'])) return '/projects';
    if (hasWord(input, ['contact', 'رابطہ', 'کانٹیکٹ'])) return '/contact';
  }

  if (hasWord(input, ['home', 'ghar', 'گھر', 'ہوم'])) return '/';
  if (hasWord(input, ['about page', 'about tab'])) return '/about';
  if (hasWord(input, ['services page', 'services tab'])) return '/services';
  if (hasWord(input, ['projects page', 'projects tab'])) return '/projects';
  if (hasWord(input, ['contact page', 'contact tab', 'contact kholo'])) return '/contact';

  return null;
}

function detectSectionScroll(input: string, currentPath: PagePath): string | null {
  for (const section of SECTIONS) {
    if (hasWord(input, section.keywords)) {
      if (section.path !== currentPath) {
        // Handled as navigation + scroll by executeAction.
        return section.id;
      }
      return section.id;
    }
  }
  return null;
}

function detectTheme(input: string): ThemeMode | null {
  const darkHints = ['dark mode', 'dark theme', 'dark karo', 'black theme', 'black mode', 'black karo', 'ڈارک', 'سیاہ', 'کالا', 'dark mode on'];
  const lightHints = ['light mode', 'light theme', 'light karo', 'white theme', 'white mode', 'white karo', 'روشن', 'سفید', 'لائٹ'];
  const systemHints = ['system theme', 'system mode', 'system karo', 'auto theme', 'سسٹم'];

  if (hasWord(input, systemHints)) return 'system';
  if (hasWord(input, darkHints)) return 'dark';
  if (hasWord(input, lightHints)) return 'light';
  return null;
}

function detectOpen(input: string): OpenTarget | null {
  if (hasWord(input, ['github', 'گٹ ہب', 'گیٹ ہب'])) return 'github';
  if (hasWord(input, ['linkedin', 'لنکڈ ان', 'لنکڈن'])) return 'linkedin';
  if (hasWord(input, ['facebook', 'فیس بک'])) return 'facebook';
  if (hasWord(input, ['whatsapp', 'واٹس ایپ'])) return 'whatsapp';
  if (hasWord(input, ['email', 'ای میل', 'e-mail', 'mail kholo'])) return 'email';
  if (hasWord(input, ['call karo', 'call kar', 'call kijiye', 'phone karo', 'کال کرو', 'فون کرو', 'کال کریں'])) return 'call';
  if (hasWord(input, ['resume', 'cv', 'ریزیوم', 'سی وی', 'resume download'])) return 'resume';
  return null;
}

/**
 * Parses free-form user input (typed or voice) into deterministic actions.
 * Pure function - safe to unit test.
 */
export function detectActions(input: string, currentPath: PagePath): AssistantAction[] {
  const text = ` ${input.toLowerCase().trim()} `;
  const actions: AssistantAction[] = [];

  if (/\bstop\b|\bchup\b|بس کرو|خاموش/.test(text)) {
    actions.push({ type: 'stopSpeaking' });
    return actions;
  }

  const theme = detectTheme(text);
  if (theme) actions.push({ type: 'theme', theme });

  const open = detectOpen(text);
  if (open) actions.push({ type: 'open', target: open });

  if (hasWord(text, ['scroll down', 'scroll karo', 'neeche', 'نیچے', 'scroll down karo'])) {
    actions.push({ type: 'scroll', direction: 'down' });
  } else if (hasWord(text, ['scroll up', 'scroll top', 'upar', 'اوپر', 'back to top'])) {
    actions.push({ type: 'scroll', direction: 'up' });
  }

  const section = detectSectionScroll(text, currentPath);
  if (section) {
    actions.push({ type: 'scrollTo', section });
  } else {
    const nav = detectNavigation(text);
    if (nav) actions.push({ type: 'navigate', path: nav });
  }

  return actions;
}

function openInNewTab(url: string): void {
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win) win.opener = null;
}

function downloadResume(): void {
  const pdfPath = `${process.env.PUBLIC_URL || ''}/Sarfraz-Ahmed-Resume.pdf`;
  fetch(pdfPath, { method: 'HEAD' })
    .then((res) => {
      if (res.ok) {
        const a = document.createElement('a');
        a.href = pdfPath;
        a.download = 'Sarfraz-Ahmed-Resume.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      downloadGeneratedResume();
    })
    .catch(() => downloadGeneratedResume());
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function downloadGeneratedResume(): void {
  const k = portfolioKnowledge;
  const projects = k.projects
    .map(
      (p) =>
        `<div class="item"><strong>${escapeHtml(p.title)}</strong> <em>[${escapeHtml(p.category)}]</em><br/><span>${escapeHtml(
          p.description,
        )}</span><br/><span class="muted">${p.tech.map(escapeHtml).join(' · ')}</span></div>`,
    )
    .join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>Sarfraz Ahmed - Resume</title>
<style>
  body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:24px auto;padding:0 20px;color:#222;line-height:1.5}
  h1{margin:0;font-size:26px} h2{font-size:16px;border-bottom:2px solid #6C63FF;padding-bottom:4px;margin-top:24px}
  .muted{color:#666;font-size:12px} .item{margin-bottom:10px} a{color:#6C63FF}
</style>
</head>
<body>
<h1>${escapeHtml(k.profile.fullName)}</h1>
<p class="muted">${escapeHtml(k.profile.role)} · ${escapeHtml(k.profile.location)} · ${escapeHtml(
    k.contact.phone,
  )} · ${escapeHtml(k.contact.email)}</p>
<p>${escapeHtml(k.profile.summary)}</p>
<h2>Experience</h2>
${k.experiences.map((e) => `<div class="item"><strong>${escapeHtml(e.title)}</strong> - ${escapeHtml(e.company)} (${escapeHtml(e.period)})<br/><span>${escapeHtml(e.description)}</span></div>`).join('')}
<h2>Key Skills</h2>
<p>${k.techStack.map(escapeHtml).join(', ')}</p>
<h2>Selected Projects</h2>
${projects}
<h2>Certificates</h2>
${k.certificates.map((c) => `<div class="item"><strong>${escapeHtml(c.organization)}</strong> - ${escapeHtml(c.title)} (${escapeHtml(c.period)})</div>`).join('')}
<h2>Contact</h2>
<p>${escapeHtml(k.contact.email)} · ${escapeHtml(k.contact.phone)} · <a href="${escapeHtml(k.socials.github)}">GitHub</a> · <a href="${escapeHtml(
    k.socials.linkedin,
  )}">LinkedIn</a></p>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Sarfraz-Ahmed-Resume.html';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function openExternal(target: OpenTarget): void {
  const link = EXTERNAL_LINKS[target];
  if (!link) return;
  if (target === 'email' || target === 'call') {
    window.location.href = link;
    return;
  }
  if (target === 'resume') {
    downloadResume();
    return;
  }
  openInNewTab(link);
}

export function executeAction(
  action: AssistantAction,
  navigate: NavigateFunction,
  currentPath: PagePath,
): void {
  switch (action.type) {
    case 'navigate':
      navigate(action.path);
      break;
    case 'scroll':
      scrollWindow(action.direction);
      break;
    case 'scrollTo': {
      const section = SECTIONS.find((s) => s.id === action.section);
      if (section && section.path !== currentPath) {
        navigate(section.path);
        window.setTimeout(() => scrollToElement(section.id), 650);
      } else {
        scrollToElement(action.section);
      }
      break;
    }
    case 'theme':
      // Theme switching is handled through the theme store so every consumer stays in sync.
      break;
    case 'open':
      openExternal(action.target);
      break;
    case 'stopSpeaking':
      // Handled by the assistant through the speech synthesis hook.
      break;
  }
}
